import { Component, Input, OnInit } from '@angular/core'
import { BlockFieldsService } from 'src/app/block-services/block-fields.service'
import { BlockDataService } from 'src/app/block-services/block-data.service'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { MixpanelService } from 'src/app/tracking/mixpanel.service'

import { CategoriesService } from 'src/app/e-commerce/categories/categories.service'
import { VendorService, Vendor } from 'src/app/multivendor/vendor.service'
import { Observable, of, from } from 'rxjs'
import { concatMap, map, reduce } from 'rxjs/operators'

@Component({
  selector: 'vendor-cats',
  templateUrl: './vendor-cats.component.html',
  styleUrls: ['./vendor-cats.component.scss']
})
export class VendorCatsComponent implements OnInit {
  // ---- UI state (same shape as stacks-categories)
  previewBlock = false
  styleFields = false
  previewFields = false

  // ---- inputs / block shell
  @Input() nestedBlock: any = {}
  @Input() importedBlockData: any
  initialData: any = false

  blockData: any
  blockId = ''

  // ---- data bags
  categoriesData: any[] = []   // filtered/ordered/limited (what template renders)
  shownCategories: any[] = []  // base list we filter from (vendor-aware)
  allCategories: any[] = []    // raw categories (for the by_id picker)
  allowedSubcategories: any[] = []
  showCategoriesProgress = false

  // ---- vendors
  vendors: Vendor[] = []
  selectedVendorId: string = 'ALL'
  private vendorCatsCache: Record<string, any[]> = {}
  private aggregatedCatsCache: any[] | null = null

  constructor(
    public blockDataService: BlockDataService,
    private blockFieldsService: BlockFieldsService,
    public builderService: BuilderService,
    private mixPanel: MixpanelService,
    public categoriesService: CategoriesService,
    private vendorService: VendorService
  ) { }

  // ---------------- lifecycle ----------------
  ngOnInit(): void {
    if (this.importedBlockData) {
      this.previewBlock = true
      this.previewFields = false
      this.styleFields = false
    }

    if (this.initialData !== 'initial') {
      if (this.nestedBlock.type === 'vendorCategories') {
        this.blockData = this.nestedBlock
        this.blockData.type = 'multivendorCats';

        this.blockId = this.nestedBlock.id
        this.previewBlock = true
      } else {
        // reuse same field factory as stacks-categories
        this.blockData = this.importedBlockData
          ? this.importedBlockData
          : this.blockFieldsService.getStacksCategoriesBlockFields()
          this.blockData.type = 'multivendorCats';

        // Use the imported block's ID if it exists, otherwise generate a new one
        if (this.importedBlockData && this.importedBlockData.id) {
          this.blockId = this.importedBlockData.id
        } else if (!this.blockId) {
          this.blockId = this.blockDataService.getUniqueId(2)
        }
      }
    } else {
      this.blockData = this.blockDataService.getSelectedBlockData(this.blockId)
      this.blockData.type = 'multivendorCats';

    }

    // load vendors list, then categories based on Data tab
    this.vendorService.vendors.subscribe((list) => {
      this.vendors = this.vendorService.getActiveVendors()
      this.syncSelectedVendorIdForBadge()
      this.getCategoriesForCurrentContext().subscribe(() => {
        // for the “Manual Selection” list we want `allCategories` filled too
        this.fillAllCategoriesList()
        this.adjustArgs(this.blockId, this.blockData.data)
      })
    })
    this.vendorService.refresh()
  }

  // -------------- block plumbing (same UX as stacks-categories) --------------
  addBlockToPreview(initial: any = '') {
    const payload = { component: VendorCatsComponent, index: 'latest', element: this, initial }
    this.blockDataService.sendBlockData(payload)
    this.builderService.blocksCollapsed = true
  }

  appendBlock($event: any) {
    if (!this.builderService.standalone) return false
    const cloned = { ...this.blockData, type: 'multivendorCats' }
    this.blockId = this.blockDataService.appendBlocksData(cloned)
    this.addBlockToPreview()
    this.builderService
      .logData('Vendor Categories Block Added', 'Block Add', 'Vendor Categories Block Added')
      .subscribe(() => { }, () => { })
    this.mixPanel.track('Block Added', {
      projectid: this.builderService.getSelectedProject(),
      block: 'vendorCategories'
    })
    return true
  }

  showData(blockId: any) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) {
      blockId = this.blockDataService.getLatestBlockId()
    }
    this.blockFieldsService.clickedBlockId = blockId
    this.blockDataService.sendCurrentBlock(VendorCatsComponent)
  }

  showStyleTab = () => { this.previewBlock = false; this.previewFields = false; this.styleFields = true }
  showDataTab = () => { this.previewBlock = false; this.previewFields = true; this.styleFields = false }

  checkSidebar = () => !this.previewBlock && !this.previewFields && !this.styleFields
  checkPreview = () => this.previewBlock && !this.previewFields && !this.styleFields
  checkData = () => this.previewFields
  checkStyle = () => this.styleFields && !this.previewFields && !this.previewBlock
  checkSettings = () => !this.checkSidebar() && !this.checkPreview()

  deleteBlock = (blockId: any) => this.blockDataService.removeBlockData(blockId)

  getBlockFields = (id: string) => {
    const fields = this.importedBlockData
      ? this.importedBlockData
      : (this.blockDataService.checkBlockAvailability(id)
        ? this.blockDataService.getSelectedBlockData(id)
        : this.blockData)

    fields.style = fields.style || {}   // ensure style exists
    return fields
  }

  // ---------------- Data tab change handlers ----------------
  queryChange(query: any, blockId: any, $event: any, type: string) {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)
    const cloned = { ...fields.data }

    let data: any = ''
    if (type === 'select') data = $event.value
    if (type === 'text') data = $event.currentTarget.value
    if (type === 'slider') data = $event.currentTarget.value

    cloned[query] = data
    fields.data = cloned

    // When switching vendor source or selected vendors → reload base list
    if (query === 'vendors' || query === 'source') {
      this.aggregatedCatsCache = null
      this.syncSelectedVendorIdForBadge()
      this.getCategoriesForCurrentContext().subscribe(() => {
        // Recompute pickers (allowedSubcategories & allCategories)
        this.fillAllCategoriesList()
        if (cloned.source === 'by_id') this.categoryChange(cloned.categories || [])
        this.adjustArgs(blockId, fields.data)
      })
      return
    }

    // For other changes, just recompute
    if (cloned.source === 'by_id' && (query === 'categories')) {
      this.categoryChange(cloned.categories || [])
    }
    this.adjustArgs(blockId, cloned)
  }

  // Inside VendorCatsComponent class
  styleChange(style: any, blockId: any, $event: any, type: string) {
    // Guard: only act on valid block
    if (!this.blockDataService.checkBlockAvailability(blockId)) return

    const fields = this.blockDataService.getSelectedBlockData(blockId)
    const clonedStyle = { ...(fields.style || {}) }

    let data = ''
    if (type === 'select') data = $event.value
    else if (type === 'text') data = $event.currentTarget.value
    else if (type === 'slider') data = $event.currentTarget.value + 'px'

    clonedStyle[style] = data
    fields.style = clonedStyle
  }

  // ---------------- vendor-aware category loading ----------------
  private getCategoriesForCurrentContext(): Observable<void> {
    const args = this.blockData?.data || {}
    const source: 'all' | 'by_id' = (args.source || 'all') as any
    const selected: string[] = Array.isArray(args.vendors) ? args.vendors : []

    const finalize = (arr: any[]) => {
      const normalized = this.normalizeCategories(arr)
      this.shownCategories = normalized
    }

    // Aggregate ALL vendors (or fallback to current project if no vendors)
    if (source === 'all' || (source === 'by_id' && !selected.length)) {
      if (this.aggregatedCatsCache) {
        finalize(this.aggregatedCatsCache.slice())
        return of(void 0)
      }

      // If you have vendors → fold over all vendors, else read current project
      if (this.vendors.length) {
        this.showCategoriesProgress = true
        return from(this.vendors).pipe(
          concatMap((v) => this.getVendorCats(v.id).pipe(map((cats) => this.attachVendorMeta(cats, v)))),
          reduce((acc, list) => acc.concat(list), [] as any[]),
          map((merged) => {
            this.aggregatedCatsCache = merged
            finalize(merged)
            this.showCategoriesProgress = false
          })
        )
      }

      // fallback to current project categories
      this.showCategoriesProgress = true
      return new Observable<void>((observer) => {
        this.categoriesService.getCategories().subscribe(
          (resp: any) => {
            finalize(this.flattenFirebase(resp))
            this.showCategoriesProgress = false
            observer.next(); observer.complete()
          },
          () => {
            finalize([])
            this.showCategoriesProgress = false
            observer.next(); observer.complete()
          }
        )
      })
    }

    // BY SELECTED VENDORS
    if (selected.length === 1) {
      const id = selected[0]
      this.showCategoriesProgress = true
      return this.getVendorCats(id).pipe(
        map((cats) => {
          const v = this.vendors.find((x) => x.id === id) || null
          finalize(this.attachVendorMeta(cats, v))
          this.showCategoriesProgress = false
        })
      )
    }

    // multiple vendors selected
    this.showCategoriesProgress = true
    return from(selected).pipe(
      concatMap((id) => {
        const v = this.vendors.find((x) => x.id === id) || null
        return this.getVendorCats(id).pipe(map((cats) => this.attachVendorMeta(cats, v)))
      }),
      reduce((acc, list) => acc.concat(list), [] as any[]),
      map((merged) => {
        finalize(merged)
        this.showCategoriesProgress = false
      })
    )
  }

  private getVendorCats(projectId: string): Observable<any[]> {
    if (this.vendorCatsCache[projectId]) return of(this.vendorCatsCache[projectId])
    return new Observable<any[]>((observer) => {
      this.categoriesService.getVendorCats(projectId).subscribe(
        (resp) => {
          const arr = this.flattenFirebase(resp)
          this.vendorCatsCache[projectId] = arr
          observer.next(arr); observer.complete()
        },
        (err) => observer.error(err)
      )
    })
  }

  // ---------------- adjust/limit/order (matches stacks-categories) ---------------
  adjustArgs = (blockId: any, args: any) => {
    let filteredData: any[] = []

    const limit = args?.limit
    const categories = args?.categories || []
    const subCats = args?.sub_categories || []
    const type = args?.source
    const order_by = args?.orderby
    const order_type = args?.order

    if (type === 'by_id' && categories.length) {
      this.categoryChange(categories)
      if (subCats && subCats.length) {
        filteredData = this.filterSubCategories(subCats)
      } else {
        filteredData = this.filterCategories(categories)
      }
    } else {
      filteredData = this.shownCategories.slice()
    }

    filteredData = this.filterLimit(limit, filteredData)
    filteredData = this.orderCategories(order_by, order_type, filteredData)

    this.setCategoriesData(blockId, filteredData)
  }

  filterLimit = (limit: any, arr: any[]) => arr.slice(0, limit || arr.length)

  filterCategories = (categories: any) =>
    this.categoriesService.findCategoryById(this.shownCategories, categories)

  filterSubCategories = (sub_categories: any) => {
    const subCatResult = this.categoriesService.findSubCategoryById(this.shownCategories, sub_categories)
    const out: any[] = []
    let count = 0
    for (const subCat of subCatResult) {
      out[count] = []
      out[count].categoryId = subCat.subCategoryId
      out[count].categoryImage = subCat.categoryImage
      out[count].categoryName = subCat.subcategory
      count++
    }
    return out
  }

  orderCategories = (order_by: string, order_type: string, filteredData: any[]) => {
    if (order_by === 'title') {
      filteredData.sort((a: any, b: any) => {
        const titleA = (a.categoryName || '').toLowerCase()
        const titleB = (b.categoryName || '').toLowerCase()
        return (order_type === 'ASC') ? titleA.localeCompare(titleB) : titleB.localeCompare(titleA)
      })
    }
    return filteredData
  }

  // ---------------- categories pickers / helpers ----------------
  categoryChange = (value: string[]) => {
    const selectedCategory = this.categoriesService.findCategoryById(this.shownCategories, value)
    this.allowedSubcategories = []
    for (const category of selectedCategory) {
      if (!category?.subcategories) continue
      for (const sub of category.subcategories) {
        this.allowedSubcategories.push(sub)
      }
    }
  }

  private fillAllCategoriesList(): void {
    // flatten currently visible vendor context into array for “Manual Selection”
    this.allCategories = this.shownCategories.slice()
  }

  private normalizeCategories(cats: any[]): any[] {
    return (cats || []).map((c) => ({
      ...c,
      categoryId: c.categoryId ?? c.id,
      categoryName: c.categoryName ?? c.name,
      categoryImage: c.categoryImage ?? c.imageUrl
    }))
  }

  private attachVendorMeta(cats: any[], vendor: Vendor | null): any[] {
    if (!cats?.length) return []
    const id = (vendor?.id ?? 'ALL').toString()
    const name = (vendor?.name || vendor?.id || 'ALL').toString()
    return this.normalizeCategories(cats).map((c) => ({
      ...c,
      _vendorId: id,
      _vendorName: name
    }))
  }

  private flattenFirebase(resp: any): any[] {
    if (!resp) return []
    const out: any[] = []
    for (const key in resp) {
      if (!Object.prototype.hasOwnProperty.call(resp, key)) continue
      out.push({
        ...resp[key],
        id: key,
        categoryId: resp[key]?.categoryId ?? key
      })
    }
    return out
  }

  private syncSelectedVendorIdForBadge() {
    const d = this.blockData?.data || {}
    this.selectedVendorId = (d.source === 'by_id' && Array.isArray(d.vendors) && d.vendors.length === 1)
      ? (d.vendors[0] || 'ALL')
      : 'ALL'
  }

  // ---------------- block data getters/setters ----------------
  getCategoriesData = (blockId: string) => {
    if (this.importedBlockData) return this.importedBlockData.categoriesData
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)
    return fields.categoriesData
  }

  setCategoriesData = (blockId: string, data: object) => {
    if (!this.blockDataService.checkBlockAvailability(blockId)) return
    const fields = this.blockDataService.getSelectedBlockData(blockId)
    fields.categoriesData = data
  }
}
